'use client'

import { cx } from 'class-variance-authority'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { DotsThreeVerticalIcon } from '@phosphor-icons/react/dist/ssr/DotsThreeVertical'
import { Button, DropdownMenu } from '@skop-app/lib-front'
import { DynamicIcon } from '@/components/atoms/DynamicIcon'
import type { DynamicIconName } from '@/components/atoms/DynamicIcon'

type DropdownButtonProps = {
  label?: string
  labelVariant?: 'primary' | 'secondary' | 'ghost' | 'error' | 'underline'
  disabled?: boolean
  isLoading?: boolean
  items: {
    key: string
    label?: string
    name?: string
    action: () => void
    className?: string
    disabled?: boolean
    icon?: DynamicIconName
  }[]
  hideLabel?: boolean
  align?: 'start' | 'end' | 'center'
}

export const DropdownButton = (props: DropdownButtonProps) => {
  const {
    label = 'Action',
    labelVariant = 'primary',
    disabled,
    isLoading = false,
    items,
    hideLabel,
    align = 'end',
  } = props

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild disabled={disabled || isLoading}>
        {!hideLabel ? (
          <Button
            variant={labelVariant}
            className={cx(
              'focus:!outline-none focus:!ring-0 focus:!ring-offset-0',
              labelVariant === 'ghost' && 'hover:!text-primary-500',
            )}
            isLoading={{
              loading: isLoading,
              loaderClassName: 'size-4',
            }}
          >
            {label}
            <CaretDownIcon className="size-5" />
          </Button>
        ) : (
          <div className="flex h-full w-full items-center justify-end p-1">
            <DotsThreeVerticalIcon className="size-6" />
          </div>
        )}
      </DropdownMenu.Trigger>

      <DropdownMenu.Content align={align}>
        {items.map((item) => (
          <DropdownMenu.Item
            key={`${item.key}-${item.label}-${item.name}`}
            onClick={item.action}
            className={item?.className}
            disabled={item?.disabled}
          >
            {item.icon && (
              <DynamicIcon name={item.icon} className="mr-1.5 size-5" />
            )}
            <span>{item.label ?? item.name}</span>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}
